import React, { useState, useEffect } from 'react';
import { Container, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Settings, Plus, FileText } from 'lucide-react';
import EmailConfigurationForm from './EmailConfigurationForm';
import SendEmailForm from './SendEmailForm';
import { emailConfigService } from '../services/emailService';
import './Email.css';

const Email = () => {
  // Email management component
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  // Load configurations
  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await emailConfigService.getAllConfigurations();
      if (response.status && response.data) {
        setConfigurations(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load email configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigurations();
  }, []);

  const handleAddNew = () => {
    setEditingConfig(null);
    setShowForm(true);
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingConfig(null);
    loadConfigurations();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this email configuration?')) {
      try {
        await emailConfigService.deleteConfiguration(id);
        loadConfigurations();
      } catch (err) {
        setError(err.message || 'Failed to delete email configuration');
      }
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await emailConfigService.setDefaultConfiguration(id);
      loadConfigurations();
    } catch (err) {
      setError(err.message || 'Failed to set default configuration');
    }
  };

  const handleTest = async (id) => {
    try {
      setLoading(true);
      const response = await emailConfigService.testConfiguration(id);
      if (response.status) {
        alert('✅ Connection test successful!');
      } else {
        alert(`❌ Connection test failed: ${response.message}`);
      }
    } catch (err) {
      alert(`❌ Connection test failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Container fluid className="email-page">
      {/* Header */}
      <div className="email-header">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="email-page-title">Email Management</h2>
            <p className="text-muted mb-0">Manage email configurations and send emails</p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={() => navigate('/email-templates')}
              className="d-flex align-items-center gap-2"
            >
              <FileText size={18} />
              Templates
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => navigate('/email-logs')}
              className="d-flex align-items-center gap-2"
            >
              <FileText size={18} />
              Email Logs
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Email Configurations Section */}
      <div className="email-configurations-section mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h4 className="mb-1">Email Configurations</h4>
                  <p className="text-muted small mb-0">Configure SMTP servers for sending emails</p>
                </div>
                <Button
                  variant="primary"
                  onClick={handleAddNew}
                  className="d-flex align-items-center gap-2"
                >
                  <Plus size={18} />
                  Add Configuration
                </Button>
              </div>

              {loading && !configurations.length ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : configurations.length === 0 ? (
                <div className="text-center py-5">
                  <Settings size={48} className="text-muted mb-3" />
                  <p className="text-muted">No email configurations found. Click "Add Configuration" to create one.</p>
                </div>
              ) : (
                <div className="configurations-list">
                  {configurations.map((config) => (
                    <div key={config.id} className="configuration-card">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <h5 className="mb-0">{config.name}</h5>
                            {config.isDefault && (
                              <span className="badge bg-primary">Default</span>
                            )}
                            {!config.isActive && (
                              <span className="badge bg-secondary">Inactive</span>
                            )}
                          </div>
                          {config.description && (
                            <p className="text-muted mb-2">{config.description}</p>
                          )}
                          <div className="config-details">
                            <small className="text-muted">
                              <strong>Host:</strong> {config.host} | <strong>Port:</strong> {config.port} | 
                              <strong> From:</strong> {config.fromEmail}
                            </small>
                          </div>
                        </div>
                        <div className="config-actions">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleTest(config.id)}
                            disabled={loading || !config.isActive}
                          >
                            Test
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEdit(config)}
                          >
                            Edit
                          </Button>
                          {!config.isDefault && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="me-2"
                              onClick={() => handleSetDefault(config.id)}
                            >
                              Set Default
                            </Button>
                          )}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(config.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

      {/* Configuration Form Modal */}
      {showForm && (
        <EmailConfigurationForm
          show={showForm}
          onHide={handleFormClose}
          config={editingConfig}
          onSuccess={handleFormClose}
        />
      )}

      {/* Send Email Form - Below Email Management */}
      <div className="send-email-section mt-4">
        <SendEmailForm />
      </div>
    </Container>
  );
};

export default Email;

